import { useEffect, useState } from 'preact/hooks';
import Lightbox from 'yet-another-react-lightbox';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/thumbnails.css';

/**
 * Click-delegated lightbox for article gallery images. The Markdig
 * directive renderer emits gallery thumbnails as
 * <c>&lt;img class="article-gallery__image" data-pswp-src="..."&gt;</c>
 * inside an <c>.article-gallery</c> figure - no anchor wrapper because an
 * <c>&lt;a href&gt;</c> raced with our preventDefault during hydration and
 * let the browser navigate to the raw image. A plain <c>&lt;img&gt;</c>
 * has no default click action to race; the delegated listener has
 * unambiguous control.
 *
 * The Thumbnails plugin is enabled only when the gallery has 2+ images -
 * single-image galleries open as a plain lightbox with no thumbnail strip.
 *
 * Delegated click listener (vs. per-element wiring) keeps this decoupled
 * from the server-rendered HTML: any future renderer change just needs to
 * keep <c>.article-gallery img[data-pswp-src]</c> intact.
 */
interface Slide {
    src: string;
    alt: string;
}

interface LightboxState {
    slides: Slide[];
    index: number;
    open: boolean;
}

const CLOSED: LightboxState = { slides: [], index: 0, open: false };

export function ArticleGalleryLightbox() {
    const [state, setState] = useState<LightboxState>(CLOSED);

    useEffect(() => {
        function onClick(event: MouseEvent) {
            // Ignore modifier-click and middle-click - let those work normally
            // (new tab, etc.).
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
            if (event.button !== 0) return;
            const target = event.target as HTMLElement | null;
            if (!target) return;
            const img = target.closest<HTMLImageElement>('.article-gallery img[data-pswp-src]');
            if (!img) return;
            const gallery = img.closest('.article-gallery');
            if (!gallery) return;
            event.preventDefault();

            const imgs = Array.from(
                gallery.querySelectorAll<HTMLImageElement>('img[data-pswp-src]'),
            );
            const slides: Slide[] = imgs.map((el) => ({
                src: el.dataset.pswpSrc || el.getAttribute('src') || '',
                alt: el.getAttribute('alt') ?? '',
            }));
            const index = Math.max(0, imgs.indexOf(img));
            setState({ slides, index, open: true });
        }
        document.addEventListener('click', onClick);
        return () => document.removeEventListener('click', onClick);
    }, []);

    return (
        <Lightbox
            open={state.open}
            close={() => setState((s) => ({ ...s, open: false }))}
            slides={state.slides}
            index={state.index}
            plugins={state.slides.length > 1 ? [Thumbnails] : []}
        />
    );
}
